import type { FormApi } from '../../form-hooks/useFormApi';
import type { PlanFormSubmitFn } from '../../form/PlanForm/PlanForm';

import { use, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { postPlan } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import ContentObjectInformation from '../../components/ContentObjectInformation/ContentObjectInformation';
import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import Heading from '../../components/Heading/Heading';
import Loader from '../../components/Loader/Loader';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarSaveAndLeaveButton from '../../components/Topbar/buttons/TopbarSaveAndLeaveButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';

import { usePlan } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useFirstLoading from '../../hooks/useFirstLoading';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import DirtyHandlerContext from '../../contexts/DirtyHandlerContext';
import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { WarningIcon } from '../../images/shapes';

import PlanForm from '../../form/PlanForm/PlanForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';

const testId = 'add-plan';

const AddPlan = () => {
  const { t } = useTranslation();
  const jwt = useToken();
  const { space } = useSelectedSpace();

  const modal = useModals();
  const navigate = useNavigate();
  const { isRoleAdmin } = use(UserContext);
  const navigateOnSave = useRef<boolean>(false);
  const { setDirty } = use(DirtyHandlerContext);

  const { id: planId } = useParams();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { navigateGrid, gridLink } = useGridNavigate('plans', '/plans');

  const [formState, setFormState] = useState<FormApi>();

  const planOptions = useMemo(
    () => ({
      pause: !isRoleAdmin,
    }),
    [isRoleAdmin],
  );

  const {
    entity: plan,
    isLoading: planIsLoading,
    updateEntity: updatePlan,
    deleteEntity: deletePlan,
    status: planStatus,
    errors: planErrors,
  } = usePlan(planId, null, planOptions);

  const firstLoading = useFirstLoading(!planId || !planIsLoading, planId);

  useApiErrorsToast(planErrors);

  const handleSave = useCallback<PlanFormSubmitFn>(
    async (data) => {
      setIsSaving(true);
      try {
        const { body, status, ok } = await postPlan(jwt, space, data);
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('Plans.Form.Added'));
        setIsSaving(false);
        navigate(`/plans/edit/${body.id}`);
        return [[body, {}], false];
      } catch (error) {
        setIsSaving(false);

        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [[data, {}], true];
        }

        if (!error.errors || !Object.keys(error.errors).length) {
          toast.error(t('Plans.Form.CouldntAdd'));
          return [[data, {}], true];
        }

        return [
          [
            data,
            typeof error.errors === 'string'
              ? { '': error.errors }
              : error.errors,
          ],
          true,
        ];
      }
    },
    [jwt, space, navigate, t],
  );

  const handleUpdate = useCallback<PlanFormSubmitFn>(
    async (data) => {
      setIsSaving(true);
      try {
        const { body, status, ok } = await updatePlan(data);
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('Plans.Form.Updated'));
        setIsSaving(false);
        return [[data, {}], false];
      } catch (error) {
        setIsSaving(false);

        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [[data, {}], true];
        }

        if (!error.errors || !Object.keys(error.errors).length) {
          toast.error(t('Plans.Form.CouldntUpdate'));
          return [[data, {}], true];
        }

        return [
          [
            data,
            typeof error.errors === 'string'
              ? { '': error.errors }
              : error.errors,
          ],
          true,
        ];
      }
    },
    [t, updatePlan],
  );

  const handleDeletePlan = useCallback(async () => {
    modal.deleting('delete-modal');
    try {
      const { body, status, ok } = await deletePlan();
      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      toast.success(t('Plans.Form.Deleted'));

      setDirty(false);
      navigateGrid();
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
        return;
      }
      toast.error(t('Plans.Form.CouldntDelete'));
    }
  }, [deletePlan, modal, navigateGrid, setDirty, t]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    await modal.delete(t('Plans.ConfirmDelete'), 'delete-modal', () =>
      handleDeletePlan(),
    );
    setIsDeleting(false);
  }, [handleDeletePlan, modal, t]);

  const isFormDisabled = isSaving || isDeleting;

  const showForm = !firstLoading && (plan || !planId) && isRoleAdmin;

  const noAccess = useMemo(() => {
    return (
      <Heading
        level={2}
        additionalClasses="text-3xl md:text-4xl leading-8 dark:text-white"
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon className="text-red w-14 md:w-20 mb-3" />
          {t('Global.NoAccess')}
        </div>
      </Heading>
    );
  }, [t]);

  const emptyResult = useMemo(() => {
    if (!isRoleAdmin) {
      return noAccess;
    }

    if (showForm) return null;
    if (firstLoading) {
      return (
        <Loader
          size="small"
          type="spinner-grid"
          {...getTestProps(testId, 'loader', 'testId')}
        />
      );
    }

    return (
      <Heading
        level={2}
        additionalClasses="text-3xl md:text-4xl leading-8 dark:text-white"
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon
            className="text-red w-14 md:w-20 mb-3"
            title={t('Plans.CouldntFind')}
          />
          {planStatus === 404
            ? t('Plans.Form.CouldntFind', { planId })
            : t('Form.CommunicationErrorMessage')}
        </div>
      </Heading>
    );
  }, [firstLoading, isRoleAdmin, noAccess, planId, planStatus, showForm, t]);

  return (
    <PageLayout
      page="plans"
      title={!planId ? t('Plans.Form.AddPlan') : t('Plans.Form.EditPlan')}
      buttonsDisabled={isSaving || isDeleting}
      breadcrumbs={
        <TopbarBreadcrumbs
          parentTitle={t('Global.Plans')}
          parentLink={gridLink}
        />
      }
      buttons={
        isRoleAdmin ? (
          <>
            <TopbarCancelButton isDirty={formState?.dirty} link={gridLink} />
            <TopbarSaveButton
              form="plan-form"
              isLoading={isSaving}
              navigateOnSave={navigateOnSave}
            />
            <TopbarActionMenu>
              <TopbarSaveAndLeaveButton
                form="plan-form"
                navigateOnSave={navigateOnSave}
              />
              {planId && <TopbarDeleteButton onClick={handleDelete} />}
            </TopbarActionMenu>
          </>
        ) : null
      }
      testId={testId}
    >
      {showForm ? (
        <div className={predefinedLayoutClasses.withSidebar}>
          <div className={predefinedLayoutClasses.leftColumnWhite}>
            <PlanForm
              plan={plan}
              onSubmit={planId ? handleUpdate : handleSave}
              disabled={isFormDisabled}
              navigateOnSave={navigateOnSave}
              setFormState={setFormState}
              testId={testId}
            />
          </div>
          <div className={predefinedLayoutClasses.rightColumn}>
            <ContentObjectInformation
              createdAt={plan?.createdAt}
              updatedAt={plan?.updatedAt}
              testId={testId}
            />
          </div>
        </div>
      ) : (
        <div className={predefinedLayoutClasses.whiteBox}>{emptyResult}</div>
      )}
    </PageLayout>
  );
};
export default AddPlan;
